import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { PublicHeader, PublicFooter } from '../components/PublicLayout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Default brand colors (fallback)
const DEFAULT_BRAND_BLUE = '#2B4B6A';
const DEFAULT_BRAND_ORANGE = '#F58A5A';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const [publicTheme, setPublicTheme] = useState(null);
  const [countdown, setCountdown] = useState(5);
  
  const token = searchParams.get('token');
  
  // Determine redirect destination - dashboard if logged in, login if not
  const redirectPath = user ? '/admin/dashboard' : '/login';

  // Fetch public theme settings
  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const res = await api.get('/settings/public-theme');
        setPublicTheme(res.data);
      } catch (err) {
        // Use defaults if theme fetch fails
      }
    };
    fetchTheme();
  }, []);

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setError('Invalid verification link');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      await api.post('/auth/verify-email', { token });
      setStatus('success');
      
      // If user is logged in, refresh their data to update email_verified status
      if (user) {
        await refreshUser();
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Verification failed. The link may have expired.');
    }
  };

  // Auto-redirect countdown after successful verification
  useEffect(() => {
    if (status === 'success') {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate(redirectPath);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [status, navigate, redirectPath]);

  // Get theme colors
  const primaryColor = publicTheme?.buttons?.primary_color || DEFAULT_BRAND_BLUE;
  const accentColor = publicTheme?.buttons?.secondary_color || DEFAULT_BRAND_ORANGE;
  const pageBackground = publicTheme?.page?.background_color || '#f9fafb';
  const cardBackground = publicTheme?.cards?.background_color || '#ffffff';

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: pageBackground }}>
      {/* Header */}
      <PublicHeader />
      
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div 
          className="max-w-md w-full rounded-2xl shadow-sm border border-gray-100 p-8 text-center"
          style={{ backgroundColor: cardBackground }}
        >
          {status === 'verifying' && (
            <>
              <div 
                className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <Loader2 className="w-10 h-10 animate-spin" style={{ color: primaryColor }} />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Verifying Your Email</h1>
              <p className="text-gray-500 text-sm">Please wait while we verify your email address...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Email Verified!</h1>
              <p className="text-gray-500 text-sm mb-2">
                Your email has been successfully verified. You can now access all features of your account.
              </p>
              <p className="text-green-600 font-bold text-sm mb-6">
                Redirecting to {user ? 'dashboard' : 'login'} in {countdown} seconds...
              </p>
              <Link
                to={redirectPath}
                className="inline-block text-white px-6 py-3 rounded-xl font-medium transition hover:shadow-lg hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
                data-testid="continue-to-login"
              >
                Continue to {user ? 'Dashboard' : 'Login'} Now
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Verification Failed</h1>
              <p className="text-gray-500 text-sm mb-6">{error}</p>
              <div className="space-y-3">
                <Link
                  to="/login"
                  className="block text-white px-6 py-3 rounded-xl font-medium transition hover:shadow-lg hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                >
                  Back to Login
                </Link>
                <p className="text-sm text-gray-500">
                  Need a new verification link?{' '}
                  <Link to="/login" style={{ color: accentColor }} className="font-medium hover:underline">
                    Login to resend verification email
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default VerifyEmailPage;
